/* -------------------------- Design imports start -------------------------- */
import {
  Edit,
  CheckCircle,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMoreRounded,
  HelpOutline,
} from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material'
import { Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
/* --------------------------- Design imports end --------------------------- */
/* ------------------------ Functional imports start ------------------------ */
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueries } from '@tanstack/react-query'

import Button from '../../../../../../components/Button'
import FileInput from '../../../../../../components/inputs/FileInput'
import MultilevelDrawer from '../../../../../../components/layout/MultilevelDrawer'
import InputFileList from '../../../../../../components/widgets/InputFileList'
import LogTool from '../../../../../../logger/logTools'
import { missingRequiredFormFields, isPrivileged } from '../../../../../../utils/functions'
import { useUserContext } from '../../../../../../utils/context'
import {
  type FileInfo,
  type FileUploadStatus,
  type SelectOption,
} from '../../../../../../utils/types'
import { Form, type FormObject, type FormFieldType } from '../../../../../Form'
import GetConstant from '../../../../utils/constants'
import {
  type Request,
  type ArticleWithQuantity,
  type BOMArticleGroup,
} from '../../../../utils/types'
import { deleteFileFromUrl, uploadFileToUrl } from '../../../../../Filesystem/api/fileApi'
import ArticleDrawer from '../../../../../Item/ArticleDrawer'
import { getFormObjectFromArticle } from '../../../../../Item/utils/functions'
import { type ArticleFormInput, type Article, Part } from '../../../../../Item/utils/types'
import { useArticle } from '../../../../../Item/hooks/useArticleQueries'
import { articleKeys } from '../../../../../Item/utils/queryKeys'
import { useUpdateArticle } from '../../../../../Item/hooks/useArticleMutations'
import { fetchArticle } from '../../../../../Item/api/articleApi'
import { fetchBOMLeafArticles, type BOMLeafArticle } from '../../../../../Bom/api/bomApi'

/* ------------------------- Functional imports end ------------------------- */

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  formState: {
    state: Partial<Request> | undefined
    setState: React.Dispatch<React.SetStateAction<Partial<Request> | undefined>>
  }
  onClose?: () => void
  onCancel?: () => void
  onCreateConfirm?: (a: Partial<Request> | undefined) => Promise<string | void>
  enableEditMode?: boolean
  onEditConfirm?: (input: any) => Promise<void>
}

/* -------------------------------------------------------------------------- */
/*                               Start component                              */
/* -------------------------------------------------------------------------- */
export default function RequestDrawer(props: Props) {
  /* -------------------------- Non state data start -------------------------- */
  const {
    open,
    setOpen,
    formState,
    onClose = () => null,
    onCancel = () => null,
    onCreateConfirm = () => null,
    enableEditMode = false,
    onEditConfirm = () => alert('TODO: provide a onEditConfirm callback!'),
  } = props

  const { t } = useTranslation(['request', 'common'])
  const log = new LogTool({ context: 'RequestDrawer', enable: true, logLevel: 'debug' })
  const { user } = useUserContext()
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [waitingOnAction, setWaiting] = useState(false)
  const [prevOpen, setPrevOpen] = useState<boolean>(false)
  const [openArticleDrawer, setOpenArticleDrawer] = useState<boolean>(false)
  const [editingArticleSelf, setEditingArticleSelf] = useState<string | null>(null)
  const [isLoadingBOMLeaves, setIsLoadingBOMLeaves] = useState(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const [fileInput, setFileInput] = useState<FileInfo[]>([])
  const [articleFormInput, setArticleFormInput] = useState<ArticleFormInput | undefined>(undefined)
  // Key format: "bomRootSelf::articleSelf" for BOM articles, "regular::articleSelf" for regular articles
  const [articleQuantities, setArticleQuantities] = useState<Record<string, number>>({})
  // Additional quantities for extra parts beyond BOM quantity
  const [additionalQuantities, setAdditionalQuantities] = useState<Record<string, number>>({})
  // Customer price suggestion validation error
  const [customerPriceError, setCustomerPriceError] = useState(false)

  // BOM article groups (BOM root -> leaf articles)
  const [bomArticleGroups, setBomArticleGroups] = useState<BOMArticleGroup[]>([])
  // Regular articles (non-BOM)
  const [regularArticles, setRegularArticles] = useState<ArticleWithQuantity[]>([])

  /**
   * Generate a unique key for an article quantity entry.
   * For BOM articles: "bomRootSelf::articleSelf"
   * For regular articles: "regular::articleSelf"
   */
  const getArticleQuantityKey = useCallback((articleSelf: string, bomRootSelf?: string): string => {
    if (bomRootSelf) {
      return `${bomRootSelf}::${articleSelf}`
    }
    return `regular::${articleSelf}`
  }, [])

  /**
   * Generate a unique key for additional quantity entries.
   */
  const getAdditionalQuantityKey = useCallback(
    (articleSelf: string, bomRootSelf?: string): string =>
      `additional::${getArticleQuantityKey(articleSelf, bomRootSelf)}`,
    [getArticleQuantityKey]
  )

  /**
   * Validate customer price input - only allow numbers, dots, commas, and spaces.
   */
  const isValidPriceInput = (value: string): boolean => {
    const trimmedValue = value.trim()
    if (trimmedValue === '') {
      return true
    }
    return /^[0-9][0-9.,\s]*$/.test(trimmedValue)
  }

  /**
   * Build a stable key for article arrays to detect real changes.
   */
  const buildArticlesKey = useCallback((articles?: Array<Article | string | null>): string => {
    if (!articles || articles.length === 0) return ''
    return articles
      .map(article => (typeof article === 'string' ? article : article?.self || ''))
      .filter(Boolean)
      .sort()
      .join(',')
  }, [])

  // Extract articles from formState - support both single article (backward compat) and multiple articles
  const articleSelfs = useMemo(() => {
    const articles = formState.state?.articles
    const article = formState.state?.article

    log.debug('articleSelfs computing:', {
      hasArticles: !!articles,
      articlesLength: Array.isArray(articles) ? articles.length : 0,
      hasArticle: !!article,
    })

    if (articles && Array.isArray(articles) && articles.length > 0) {
      // Multiple articles - extract self URLs from Article objects or use string URLs directly
      const selfs = articles
        .map(a => {
          if (typeof a === 'string') {
            return a
          }
          // If it's an Article object, get the self URL
          return a?.self || ''
        })
        .filter((self): self is string => Boolean(self))
      log.debug('articleSelfs result (from articles):', selfs)
      return selfs
    } else if (article) {
      // Single article (backward compatibility)
      const self = typeof article === 'string' ? article : article?.self
      const result = self ? [self] : []
      log.debug('articleSelfs result (from article):', result)
      return result
    }
    log.debug('articleSelfs result: empty')
    return []
  }, [formState.state?.articles, formState.state?.article])

  // Fetch all articles with files
  const articleQueries = useQueries({
    queries: articleSelfs.map(self => ({
      queryKey: articleKeys.detail(self, { expand: ['files'] }),
      queryFn: () => fetchArticle(self, ['files']),
      enabled: open && !!self,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    })),
  })

  // Get articles from queries (fetched articles) - memoize based on actual query data changes
  // Create a stable key from query results to prevent unnecessary recalculations
  const fetchedArticlesKey = articleQueries
    .map(q => (q.isSuccess && q.data ? q.data.self : ''))
    .join(',')

  const fetchedArticlesData = useMemo(
    () =>
      articleQueries
        .filter(query => query.isSuccess && query.data)
        .map(query => query.data as Article),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchedArticlesKey]
  )

  // Also check if articles were passed as full objects in formState
  const passedArticlesData = useMemo(() => {
    const articles = formState.state?.articles
    log.debug('passedArticlesData processing:', {
      articlesExists: !!articles,
      isArray: Array.isArray(articles),
      length: Array.isArray(articles) ? articles.length : 0,
      firstArticle: Array.isArray(articles) && articles.length > 0 ? articles[0] : null,
    })
    if (articles && Array.isArray(articles)) {
      const filtered = articles.filter(
        a =>
          typeof a !== 'string' &&
          a !== null &&
          typeof a === 'object' &&
          'self' in a &&
          a.self !== undefined &&
          a.self !== '' // Also filter out empty strings
      ) as Article[]
      log.debug('passedArticlesData filtered result:', filtered.length, filtered)
      return filtered
    }
    return []
  }, [formState.state?.articles])

  // Combine fetched and passed articles
  // Always prefer fetched articles (they have files expanded) over passed articles
  // This ensures we use the most up-to-date data with all files loaded
  const articlesData = useMemo(() => {
    // Create a map of fetched articles by self URL (these have files expanded)
    const fetchedMap = new Map<string, Article>()
    fetchedArticlesData.forEach(article => {
      if (article.self) {
        fetchedMap.set(article.self, article)
      }
    })

    // Start with fetched articles (they have files expanded)
    const result: Article[] = [...fetchedArticlesData]

    // Add passed articles that aren't already in fetched articles
    // This ensures we show all articles even if some queries haven't loaded yet
    passedArticlesData.forEach(article => {
      if (article.self && !fetchedMap.has(article.self)) {
        result.push(article)
      } else if (article.self && fetchedMap.has(article.self)) {
        // If we have a fetched version, replace the passed one with the fetched one
        // This ensures we always use the version with expanded files
        const index = result.findIndex(a => a.self === article.self)
        if (index >= 0) {
          result[index] = fetchedMap.get(article.self)!
        }
      }
    })

    return result
  }, [fetchedArticlesData, passedArticlesData])

  const isLoadingArticles = articleQueries.some(query => query.isLoading)
  const hasArticleErrors = articleQueries.some(query => query.isError)

  // For backward compatibility: single article handling
  const articleSelf = articleSelfs[0]
  const articleQuery = useArticle(
    articleSelf || '',
    { expand: ['files'] },
    !!articleSelf && open && articleSelfs.length === 1 && !!articleSelf
  )
  const articleData = articleQuery.data

  const updateArticleMutation = useUpdateArticle()
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    if (open && !prevOpen) {
      setPrevOpen(true)
      setArticleQuantities({})
    }
  }, [open, prevOpen])

  // Create a stable key from article self URLs to detect when articles actually change
  const articlesKey = useMemo(() => {
    return articlesData
      .map(a => a.self)
      .filter(Boolean)
      .sort()
      .join(',')
  }, [articlesData])

  // Track which articles have been processed to avoid re-fetching
  const processedArticlesRef = React.useRef<string>('')

  // Process articles: separate BOM root articles and fetch their leaf articles
  useEffect(() => {
    log.debug('processArticles effect running', {
      open,
      articlesDataLength: articlesData.length,
      articlesKey,
      processedArticlesRefCurrent: processedArticlesRef.current,
    })

    if (!open || articlesData.length === 0) {
      log.debug('processArticles early return: not open or no articlesData')
      setBomArticleGroups([])
      setRegularArticles([])
      processedArticlesRef.current = ''
      return
    }

    // Skip if we've already processed these exact articles
    if (processedArticlesRef.current === articlesKey) {
      log.debug('processArticles skip: already processed this articlesKey')
      return
    }

    const processArticles = async () => {
      log.info('processArticles starting async processing')
      setIsLoadingBOMLeaves(true)
      const newBomGroups: BOMArticleGroup[] = []
      const newRegularArticles: ArticleWithQuantity[] = []
      const newQuantities: Record<string, number> = {}

      for (const article of articlesData) {
        if (!article.self) continue

        // Check if this is a BOM base article
        if (article.isBomBase) {
          try {
            // Fetch leaf articles for this BOM
            const leafArticles = await fetchBOMLeafArticles(article.self, ['files'])

            const articlesWithQuantity: ArticleWithQuantity[] = leafArticles.map(
              (leaf: BOMLeafArticle) => {
                const baseQuantity = leaf.bom_amount || 1
                // Set initial quantity from BOM amount using composite key (bomRoot::article)
                if (leaf.self && article.self) {
                  const quantityKey = getArticleQuantityKey(leaf.self, article.self)
                  newQuantities[quantityKey] = baseQuantity
                }

                return {
                  article: {
                    self: leaf.self,
                    name: leaf.name,
                    number: leaf.number,
                    files: leaf.files,
                    hasChildren: leaf.hasChildren,
                    isBomBase: leaf.isBomBase,
                    material: leaf.material,
                    key: leaf.self,
                    lastModifiedAt: new Date(),
                  } as Article,
                  quantity: baseQuantity,
                  baseQuantity: baseQuantity, // Store original BOM amount
                  bomRootName: article.name || article.number,
                  bomRootSelf: article.self,
                  parentPath: leaf.parent_path,
                }
              }
            )

            // Set initial BOM root quantity to 1
            const bomRootQuantityKey = getArticleQuantityKey(article.self, 'bomRoot')
            newQuantities[bomRootQuantityKey] = 1

            // Always add the BOM group, even if there are no leaf articles
            // (the root article itself needs to be shown)
            newBomGroups.push({
              bomRootName: article.name || article.number,
              bomRootSelf: article.self,
              bomRootArticle: article, // Store the full BOM root article
              bomRootQuantity: 1, // Initial multiplier
              articles: articlesWithQuantity,
            })
          } catch (error) {
            log.error('Error fetching BOM leaf articles for:', article.self, error)
            // If fetching fails, treat it as a regular article
            const quantityKey = getArticleQuantityKey(article.self)
            newQuantities[quantityKey] = 1
            newRegularArticles.push({
              article,
              quantity: 1,
            })
          }
        } else {
          // Regular article (non-BOM) - use "regular::articleSelf" as key
          const quantityKey = getArticleQuantityKey(article.self)
          newQuantities[quantityKey] = 1
          newRegularArticles.push({
            article,
            quantity: 1,
          })
        }
      }

      // Mark these articles as processed
      processedArticlesRef.current = articlesKey

      log.info('processArticles completed', {
        newBomGroupsCount: newBomGroups.length,
        newRegularArticlesCount: newRegularArticles.length,
        newQuantities,
      })

      setBomArticleGroups(newBomGroups)
      setRegularArticles(newRegularArticles)
      setArticleQuantities(newQuantities)
      setIsLoadingBOMLeaves(false)
    }

    void processArticles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articlesKey, open, getArticleQuantityKey])

  // Update article form input when article data is fetched (for backward compatibility with single article)
  useEffect(() => {
    if (articleData && openArticleDrawer && articleSelfs.length === 1 && !editingArticleSelf) {
      // Backward compatibility: single article
      // Only update if the article self URL changed or if articleFormInput is undefined
      if (!articleFormInput || articleFormInput.self !== articleData.self) {
        const articleFormData = getFormObjectFromArticle(articleData)
        // Preserve newVersion and versionComment if they exist
        setArticleFormInput(prev => ({
          ...articleFormData,
          ...(prev && { newVersion: prev.newVersion, versionComment: prev.versionComment }),
        }))
      }
    }
  }, [
    articleData,
    openArticleDrawer,
    articleSelfs.length,
    editingArticleSelf,
    articleFormInput?.self,
  ])

  // Update request articles when articles are updated
  useEffect(() => {
    // Only run when drawer is open
    if (!open) return

    if (articlesData.length > 0) {
      const nextArticlesKey = buildArticlesKey(articlesData)
      formState.setState(prev => {
        if (prev?.articles) {
          const prevArticlesKey = buildArticlesKey(prev.articles as Array<Article | string>)
          if (prevArticlesKey === nextArticlesKey) {
            return prev
          }
          return {
            ...prev,
            articles: articlesData,
          } as Request
        }
        return prev
      })
    } else if (articleData && articleSelfs.length === 1) {
      // Backward compatibility: single article
      formState.setState(prev => {
        if (prev?.article && typeof prev.article !== 'string') {
          if (prev.article.self === articleData.self) {
            return prev
          }
          return {
            ...prev,
            article: articleData,
          } as Request
        }
        return prev
      })
    }
  }, [open, articlesData, articleData, articleSelfs.length])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  /**
   * Setter for the uploadStatus property on a FileInfo object within this component.
   * Do not use this function if you want to update multiple files at once as the component is rerendered after every status update!
   * @param file the file on which the uploadStatus should be updated.
   * @param status the updated uploadStatus.
   */
  function setFileUploadStatus(file: FileInfo, status: FileUploadStatus) {
    setFileInput((prev: FileInfo[]) =>
      prev.map((listedFile: FileInfo) => {
        if (listedFile.name === file.name) {
          // add an upload-status of success to the file info object or overwrite it with the provided status
          return Object.assign(file, { uploadStatus: status })
        }
        return listedFile
      })
    )
  }

  /**
   * Wrapper for the uploadFile function that specifies the onSuccess and onError callbacks.
   * This function simply allows us to upload files using the same behavior at multiple places
   * within this component without rewriting the callback functions all the time.
   * @param file the file to upload.
   * @param url the url to upload the file to.
   */
  async function uploadRequestFile(file: FileInfo, url: string) {
    try {
      const uploadedFile = await uploadFileToUrl(url, file as File)
      setFileInput(prev =>
        prev.map((listedFile: FileInfo) => {
          if (listedFile.name === file.name) {
            return { ...uploadedFile, uploadStatus: 'success' }
          }
          return listedFile
        })
      )
    } catch (error) {
      log.error('Error uploading request file', error)
      setFileUploadStatus(file, 'uploadError')
    }
  }

  /**
   * Check if a single Article has at least one file (3D or technical drawing)
   * @param article - The article to check
   * @returns true if Article has valid files, false otherwise
   */
  const articleHasFiles = (article: Article): boolean => {
    if (!article) {
      return false
    }

    const files = article.files
    if (!files || typeof files === 'string' || files.length === 0) {
      return false
    }

    // Check if at least one file is a 3D model or technical drawing
    const hasValidFile = files.some((file: FileInfo) => {
      const fileName = file.name || file.key || ''
      const fileExt = fileName.split('.').pop()?.toLowerCase() || ''

      // Check if it's a technical drawing (contains "__technical_drawing" in name)
      if (fileName.includes('__technical_drawing')) {
        return true
      }

      // Supported formats from UnauthS3FileUpload (from commented accept string in UnauthS3FileUpload.tsx)
      // Formats: .stl, .stp, .step, .iges, .igs, .3mf, .e57, .example, .gltf-binary, .JT, .mesh, .mtl, .obj, .prc, .u3d, .vnd, vrml, .dxf, .dwg, .sat, .sab, .pdf, .xlsx, .doc, .docx, .ppt, .pptx, .xls, .jpg, .jpeg, .png, .gif, .bmp, .tiff, .tif, .svg, .mp3, .mp4, .wav, .avi, .mov, .wmv, .flv, .mkv, .webm, .mpg, .mpeg, .m4v
      const supportedFormats = [
        // 3D CAD formats
        'stl',
        'stp',
        'step',
        'iges',
        'igs',
        '3mf',
        'e57',
        'example',
        'gltf-binary',
        'jt',
        'mesh',
        'mtl',
        'obj',
        'prc',
        'u3d',
        'vnd',
        'vrml',
        'dxf',
        'dwg',
        'sat',
        'sab',
        // Document formats
        'pdf',
        'xlsx',
        'doc',
        'docx',
        'ppt',
        'pptx',
        'xls',
        // Image formats
        'jpg',
        'jpeg',
        'png',
        'gif',
        'bmp',
        'tiff',
        'tif',
        'svg',
        // Audio formats
        'mp3',
        'wav',
        // Video formats
        'mp4',
        'avi',
        'mov',
        'wmv',
        'flv',
        'mkv',
        'webm',
        'mpg',
        'mpeg',
        'm4v',
        // Additional 3D formats (from existing list)
        '3dm',
        '3ds',
        'ipt',
        'iam',
        'model',
        'session',
        'dlv',
        'exp',
        'catpart',
        'catproduct',
        'catshape',
        'cgr',
        '3dxml',
        'asm',
        'neu',
        'prt',
        'xas',
        'xpr',
        'mf1',
        'arc',
        'unv',
        'pkg',
        'ifc',
        'ifczip',
        'x_b',
        'x_t',
        'xmt',
        'xmt_txt',
        'par',
        'pwd',
        'psm',
        'sldasm',
        'sldprt',
        'stpz',
        'stpx',
        'stpxz',
        'vda',
      ]

      return supportedFormats.includes(fileExt)
    })

    return hasValidFile
  }

  /**
   * Check if all articles have at least one file (3D or technical drawing)
   * Checks BOM root articles, BOM leaf articles, and regular articles
   * @returns true if all articles have valid files, false otherwise
   */
  const hasAllArticlesFiles = useCallback((): boolean => {
    // Check BOM groups (root and leaf articles)
    for (const group of bomArticleGroups) {
      // Check BOM root article files
      if (!articleHasFiles(group.bomRootArticle)) {
        return false
      }
      // Check BOM leaf articles
      for (const item of group.articles) {
        if (!articleHasFiles(item.article)) {
          return false
        }
      }
    }

    // Check regular articles
    for (const item of regularArticles) {
      if (!articleHasFiles(item.article)) {
        return false
      }
    }

    // If no processed articles yet, check articlesData for backward compatibility
    if (bomArticleGroups.length === 0 && regularArticles.length === 0) {
      if (articlesData.length > 0) {
        return articlesData.every(article => articleHasFiles(article))
      }

      // Backward compatibility: single article
      const article: Article | Part | string | undefined = formState.state?.article
      if (!article || typeof article === 'string') {
        return false
      }
      return articleHasFiles(article as Article)
    }

    return bomArticleGroups.length > 0 || regularArticles.length > 0
  }, [bomArticleGroups, regularArticles, articlesData, formState.state?.article])

  /**
   * Check if Article has at least one file (3D or technical drawing) - backward compatibility
   * @returns true if Article has valid files, false otherwise
   */
  const hasArticleFiles = (): boolean => {
    return hasAllArticlesFiles()
  }

  /**
   * Get all articles (both BOM leaf and regular) for display and validation
   */
  const getAllDisplayArticles = useCallback((): ArticleWithQuantity[] => {
    const all: ArticleWithQuantity[] = []

    // Add BOM leaf articles
    for (const group of bomArticleGroups) {
      all.push(...group.articles)
    }

    // Add regular articles
    all.push(...regularArticles)

    return all
  }, [bomArticleGroups, regularArticles])

  /**
   * Check if all required FormFields are filled in correctly.
   * @returns true if form is invalid (should be disabled), false if valid
   */
  const isFormDisabled = (): boolean => {
    // Check if all articles have valid quantities (>= 1)
    // For BOM articles, use composite key (bomRoot::article)
    // For regular articles, use "regular::article"

    // Check BOM articles
    for (const group of bomArticleGroups) {
      for (const item of group.articles) {
        if (!item.article.self) continue
        const quantityKey = getArticleQuantityKey(item.article.self, group.bomRootSelf)
        const quantity = articleQuantities[quantityKey]
        if (!quantity || quantity < 1) {
          return true
        }
      }
    }

    // Check regular articles
    for (const item of regularArticles) {
      if (!item.article.self) continue
      const quantityKey = getArticleQuantityKey(item.article.self)
      const quantity = articleQuantities[quantityKey]
      if (!quantity || quantity < 1) {
        return true
      }
    }

    return (
      missingRequiredFormFields([...requestFormFields], formState.state) ||
      !hasArticleFiles() ||
      isLoadingBOMLeaves
    )
  }

  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */

  /**
   * Handle BOM root quantity change.
   * When the root quantity changes, all leaf article quantities are recalculated
   * as: baseQuantity * newRootQuantity
   */
  const handleBomRootQuantityChange = useCallback(
    (bomRootSelf: string, newRootQuantity: number) => {
      log.debug('handleBomRootQuantityChange', { bomRootSelf, newRootQuantity })

      if (newRootQuantity < 1) return

      // Find the BOM group
      const group = bomArticleGroups.find(g => g.bomRootSelf === bomRootSelf)
      if (!group) return

      // Update the BOM root quantity in the group
      setBomArticleGroups(prev =>
        prev.map(g => {
          if (g.bomRootSelf === bomRootSelf) {
            return { ...g, bomRootQuantity: newRootQuantity }
          }
          return g
        })
      )

      // Update the BOM root quantity key
      const bomRootQuantityKey = getArticleQuantityKey(bomRootSelf, 'bomRoot')

      // Recalculate all leaf article quantities
      const newQuantities: Record<string, number> = {
        [bomRootQuantityKey]: newRootQuantity,
      }

      for (const item of group.articles) {
        if (!item.article.self) continue
        const baseQty = item.baseQuantity || item.quantity || 1
        const newQty = baseQty * newRootQuantity
        const quantityKey = getArticleQuantityKey(item.article.self, bomRootSelf)
        newQuantities[quantityKey] = newQty
      }

      setArticleQuantities(prev => ({
        ...prev,
        ...newQuantities,
      }))
    },
    [bomArticleGroups, getArticleQuantityKey]
  )

  /**
   * Build the articlesWithQuantities array from article quantities and BOM groups.
   * Implements BOM logic aligned with AQG completion:
   * - Always include BOM root with rootQuantity
   * - For each BOM leaf: include baseQuantity * rootQuantity + additional parts
   * - For regular articles: include base quantity + additional parts
   * - If an article exists both as BOM leaf and regular, merge quantities
   */
  const buildArticlesWithQuantities = useCallback(() => {
    log.debug('buildArticlesWithQuantities called with:', {
      bomArticleGroupsCount: bomArticleGroups.length,
      regularArticlesCount: regularArticles.length,
      articleQuantities,
      additionalQuantities,
    })

    const result: { article: string; quantity: number }[] = []

    // Track total quantities for BOM leaf articles (base * root + additional)
    const bomLeafTotals: Record<string, number> = {}

    // Process BOM groups
    for (const group of bomArticleGroups) {
      const rootQuantity = group.bomRootQuantity || 1

      for (const item of group.articles) {
        if (!item.article.self) continue

        const baseQuantity = item.baseQuantity || item.quantity || 1
        const expectedQuantity = baseQuantity * rootQuantity
        const additionalKey = getAdditionalQuantityKey(item.article.self, group.bomRootSelf)
        const additionalQty = additionalQuantities[additionalKey] ?? 0
        const totalQuantity = expectedQuantity + additionalQty
        bomLeafTotals[item.article.self] = (bomLeafTotals[item.article.self] || 0) + totalQuantity
      }

      // Always include BOM root with rootQuantity
      if (group.bomRootSelf) {
        result.push({
          article: group.bomRootSelf,
          quantity: rootQuantity,
        })
      }
    }

    // Track which BOM leaf totals were merged with regular articles
    const mergedArticles = new Set<string>()

    // Process regular articles (non-BOM)
    // Merge any BOM leaf totals for the same article
    for (const item of regularArticles) {
      if (!item.article.self) {
        log.warn('Regular article skipped - no self URL:', item.article)
        continue
      }

      // Use composite key for regular articles
      const quantityKey = getArticleQuantityKey(item.article.self)
      let quantity = articleQuantities[quantityKey] || 1

      // Add additional quantity from regular article input
      const additionalKey = getAdditionalQuantityKey(item.article.self)
      const additionalQty = additionalQuantities[additionalKey] ?? 0
      quantity += additionalQty

      // If this article has BOM leaf totals, add them to the quantity
      if (bomLeafTotals[item.article.self]) {
        log.debug('Merging BOM leaf totals into regular article:', {
          article: item.article.self,
          regularQuantity: quantity,
          bomLeafTotal: bomLeafTotals[item.article.self],
          totalQuantity: quantity + bomLeafTotals[item.article.self],
        })
        quantity += bomLeafTotals[item.article.self]
        mergedArticles.add(item.article.self)
      }

      result.push({
        article: item.article.self,
        quantity,
      })
    }

    // Add any BOM leaf totals that weren't merged with regular articles
    // These are articles that only appear in BOM but not as standalone
    for (const [articleSelf, totalQty] of Object.entries(bomLeafTotals)) {
      if (!mergedArticles.has(articleSelf)) {
        log.debug('Adding non-merged BOM leaf total:', {
          article: articleSelf,
          totalQuantity: totalQty,
        })
        result.push({
          article: articleSelf,
          quantity: totalQty,
        })
      }
    }

    log.debug('buildArticlesWithQuantities result:', result)
    return result
  }, [
    bomArticleGroups,
    regularArticles,
    articleQuantities,
    additionalQuantities,
    getArticleQuantityKey,
    getAdditionalQuantityKey,
  ])

  const handleCreateRequest = async () => {
    log.info('handleCreateRequest called')
    log.debug('handleCreateRequest formState.state -> ', formState.state)
    log.debug('handleCreateRequest articlesData -> ', articlesData)
    log.debug('handleCreateRequest bomArticleGroups -> ', bomArticleGroups)
    log.debug('handleCreateRequest regularArticles -> ', regularArticles)

    setWaiting(true)
    // set loading state on all files to loading
    if (fileInput.length > 0) {
      setFileInput((prev: FileInfo[]) =>
        prev.map(file => Object.assign(file, { uploadStatus: 'loading' }))
      )
    }

    // Build articlesWithQuantities from the current state
    const articlesWithQuantities = buildArticlesWithQuantities()

    log.info('Built articlesWithQuantities:', articlesWithQuantities)

    // If articlesWithQuantities is empty but we have articles in formState, use those as fallback
    // This handles edge cases where articles haven't been processed yet
    let effectiveArticlesWithQuantities = articlesWithQuantities
    if (
      (!articlesWithQuantities || articlesWithQuantities.length === 0) &&
      formState.state?.articles &&
      Array.isArray(formState.state.articles) &&
      formState.state.articles.length > 0
    ) {
      log.warn('articlesWithQuantities is empty, falling back to formState.state.articles')
      // Convert articles to articlesWithQuantities format with default quantity of 1
      effectiveArticlesWithQuantities = formState.state.articles
        .map((a: Article | string) => {
          const articleSelf = typeof a === 'string' ? a : a?.self
          return {
            article: articleSelf,
            quantity: 1,
          }
        })
        .filter((item: { article: string | undefined; quantity: number }) => item.article)
      log.info('Fallback articlesWithQuantities:', effectiveArticlesWithQuantities)
    }

    // Create request data with articlesWithQuantities
    const requestData = {
      ...formState.state,
      articlesWithQuantities: effectiveArticlesWithQuantities,
      // Remove the old articles field since we're using articlesWithQuantities
      articles: undefined,
      article: undefined,
    }

    log.info('Request data to send -> ', requestData)

    // posting request to server and waiting on response
    const requestFilesUrl: string | void | null = await onCreateConfirm(requestData)
    log.debug('requestFilesUrl -> ', requestFilesUrl)
    // check if the user specified files to upload !
    if (fileInput.length > 0 && requestFilesUrl) {
      // upload the files one by one for a more detailed feedback if something goes wrong,
      // and to make use parallel uploading to decrease upload time
      for (const file of fileInput) {
        await uploadRequestFile(file, requestFilesUrl)
        // response.articleFilesUrl cannot be undefined here as the server created the article successfully and therefore created an article files url

        // store the articleFilesUrl in the inputFiles state to have it available in case something goes wrong
        /* setInputFiles((prev: FileInfo[]) => prev.map(
          file => Object.assign(file, {uploadUrl: response.articleFilesUrl})
        )) */
      }
    }
    setWaiting(false)
    handleClose()
  }

  const handleUpdateRequest = () => {
    log.debug('handleUpdateRequest -> ', formState.state)
  }

  /**
   * Remove a file from fileInput list before it is uploaded
   * @param file
   */
  const handleRemoveFileFromList = (file: FileInfo) => {
    // update rendered files list
    setFileInput(prevFileList => {
      // get the location of the file to remove within the files list
      const fileIndex = prevFileList.findIndex(listedFile => listedFile.name === file.name)
      // remove the file from the list. Note that this does not work on the prevFileList directly
      const updatedFileList = [...prevFileList]
      updatedFileList.splice(fileIndex, 1)

      // return the files list where the file is removed
      return updatedFileList
    })
  }
  /**
   * (Re)upload a file to the specified url.
   * @param file The file to upload
   * @param url The url to upload the file to.
   */
  const handleReUploadFile = async (file: FileInfo, url: string) => {
    // set the uploadStatus of the current file to loading
    setFileUploadStatus(file, 'loading')
    // retry to upload the file
    await uploadRequestFile(file, url)
  }

  /**
   * Delete a file that was uploaded already.
   * @param file The file to delete.
   */
  const handleDeleteFile = async (file: FileInfo) => {
    // set the uploadStatus of the current file to loading
    setFileUploadStatus(file, 'loading')
    // try to delete the file
    try {
      await deleteFileFromUrl(file.self as string)
      handleRemoveFileFromList(file)
    } catch (error) {
      log.error('Error deleting request file', error)
      setFileUploadStatus(file, 'deleteError')
    }
  }

  const handleClose = (preventOnClose = false) => {
    // reset form state
    setWaiting(false)
    formState.setState(undefined)
    setFileInput([])
    setOpenArticleDrawer(false)
    setArticleFormInput(undefined)
    setArticleQuantities({})
    setAdditionalQuantities({})
    setCustomerPriceError(false)
    setEditingArticleSelf(null)
    setBomArticleGroups([])
    setRegularArticles([])
    setIsLoadingBOMLeaves(false)
    processedArticlesRef.current = ''

    setOpen(false)
    setPrevOpen(false)
    !preventOnClose && onClose()
  }

  const handleOpenArticleDrawer = (articleSelf?: string) => {
    // Find the article by self URL - check BOM root, BOM leaf, and regular articles
    let targetArticle: Article | undefined

    if (articleSelf) {
      // Check BOM root articles first
      for (const group of bomArticleGroups) {
        if (group.bomRootSelf === articleSelf) {
          targetArticle = group.bomRootArticle
          break
        }
        // Check leaf articles
        const found = group.articles.find(a => a.article.self === articleSelf)
        if (found) {
          targetArticle = found.article
          break
        }
      }

      // Check in regular articles
      if (!targetArticle) {
        const found = regularArticles.find(a => a.article.self === articleSelf)
        if (found) {
          targetArticle = found.article
        }
      }

      // Check in articlesData as fallback
      if (!targetArticle) {
        targetArticle = articlesData.find(a => a.self === articleSelf)
      }
    }

    if (targetArticle) {
      setEditingArticleSelf(articleSelf || null)
      const articleFormData = getFormObjectFromArticle(targetArticle)
      // Preserve newVersion and versionComment if they exist in current state
      setArticleFormInput(prev => ({
        ...articleFormData,
        ...(prev &&
          prev.self === articleFormData.self && {
            newVersion: prev.newVersion,
            versionComment: prev.versionComment,
          }),
      }))
      setOpenArticleDrawer(true)
    } else if (formState.state?.article && typeof formState.state.article !== 'string') {
      // Backward compatibility: single article
      setEditingArticleSelf(null)
      const formStateArticle: Article | Part = formState.state.article
      const articleFormData = getFormObjectFromArticle(formStateArticle as Article)
      setArticleFormInput(prev => ({
        ...articleFormData,
        ...(prev &&
          prev.self === articleFormData.self && {
            newVersion: prev.newVersion,
            versionComment: prev.versionComment,
          }),
      }))
      setOpenArticleDrawer(true)
    }
  }

  const handleArticleDrawerClose = async () => {
    setOpenArticleDrawer(false)
    const editedSelf = editingArticleSelf
    setEditingArticleSelf(null)

    // Refetch all articles to get updated files
    const refetchPromises = articleQueries.map(query => {
      if (query.data) {
        return query.refetch()
      }
      return Promise.resolve()
    })
    await Promise.all(refetchPromises)

    // Backward compatibility: refetch single article
    if (articleSelf && articleSelfs.length === 1) {
      await articleQuery.refetch()
    }

    // If we edited a BOM root article, update it in the BOM groups
    if (editedSelf) {
      const updatedArticle = articlesData.find(a => a.self === editedSelf)
      if (updatedArticle) {
        setBomArticleGroups(prev =>
          prev.map(group => {
            if (group.bomRootSelf === editedSelf) {
              return { ...group, bomRootArticle: updatedArticle }
            }
            return group
          })
        )
      }
    }

    // Force reprocessing of articles to pick up any file changes
    processedArticlesRef.current = ''
  }

  const handleUpdateArticle = async (input: any): Promise<string | void> => {
    const articleSelfToUpdate =
      editingArticleSelf ||
      (formState.state?.article && typeof formState.state.article !== 'string'
        ? formState.state.article.self
        : undefined)

    if (!articleSelfToUpdate) {
      return
    }

    try {
      await updateArticleMutation.mutateAsync({
        idOrSelf: articleSelfToUpdate,
        data: input,
      })
      // Refetch all articles to get updated files
      articleQueries.forEach(query => {
        if (query.data) {
          void query.refetch()
        }
      })
      // Backward compatibility
      if (articleSelf && articleSelfs.length === 1) {
        await articleQuery.refetch()
      }
      return articleSelfToUpdate + 'files/'
    } catch (error) {
      log.error('Error updating article:', error)
      throw error
    }
  }

  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const certificateOptions = GetConstant({ name: 'certificateOptions' }) as SelectOption[]
  const requestFormFieldsDisabled = GetConstant({
    name: 'requestFormFieldsDisabled',
  }) as FormFieldType[]
  const requestFormFields = GetConstant({
    name: 'requestFormFields',
    certificateOptions: certificateOptions,
    selectedProduct:
      articlesData.length > 0
        ? articlesData[0]?.self || ''
        : typeof formState.state?.article !== 'string' && formState.state?.article?.self
          ? formState.state?.article?.self
          : '',
  }) as FormFieldType[]
  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */
  /* ------------------------- Pre render actions end ------------------------- */

  log.debug('FormInput -> ', formState.state)

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <>
      <MultilevelDrawer
        open={open}
        setOpen={setOpen}
        onClose={handleClose}
        size="big"
        title={t('common:content.label.createRequest')}
        customActions={
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
            <Button
              id="cancelButton"
              onClick={() => setOpen(false)}
              color="inherit"
              variant="contained"
              disabled={waitingOnAction}
              sx={{ mr: 1, textTransform: 'none' }}
            >
              {t('common:interaction.button.cancel')}
            </Button>
            <LoadingButton
              id="createButton"
              variant="contained"
              sx={{ marginLeft: '10px', color: 'whitesmoke', textTransform: 'none' }}
              disabled={isFormDisabled()}
              loading={waitingOnAction}
              onClick={() => {
                if (enableEditMode) {
                  handleUpdateRequest()
                } else {
                  void (async () => await handleCreateRequest())()
                }
              }}
            >
              {enableEditMode
                ? t('common:interaction.button.save')
                : t('common:interaction.button.create')}
            </LoadingButton>
          </Box>
        }
      >
        <React.Fragment>
          {/* Articles Table Section */}
          {(isLoadingArticles || isLoadingBOMLeaves) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          )}
          {hasArticleErrors && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('request:feedback.error.loadingArticles')}
            </Alert>
          )}

          {/* BOM Article Groups - Each BOM root gets its own section with root article and leaf articles */}
          {bomArticleGroups.map((group, groupIndex) => {
            const rootQuantity = group.bomRootQuantity || 1
            const hasRootFiles = articleHasFiles(group.bomRootArticle)

            // Define columns for BOM leaf articles table
            const bomLeafColumns: ColumnsType<ArticleWithQuantity> = [
              {
                title: t('request:content.label.nameWithNumber', { defaultValue: 'Name (Nr.)' }),
                dataIndex: ['article', 'name'],
                key: 'name',
                width: 260,
                render: (_, record) => {
                  const hasFiles = articleHasFiles(record.article)
                  const articleName =
                    record.article.name ||
                    record.article.number ||
                    t('request:content.label.part', 'Part')
                  const numberSuffix =
                    record.article.name && record.article.number
                      ? ` (${record.article.number})`
                      : ''
                  const displayName = `${articleName}${numberSuffix}`

                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title={record.parentPath?.join(' > ') || ''}>
                        <span>{displayName}</span>
                      </Tooltip>
                      {hasFiles ? (
                        <Chip
                          icon={<CheckCircle />}
                          label={t('request:content.label.hasFiles')}
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<ErrorIcon />}
                          label={t('request:content.label.noFiles')}
                          color="error"
                          size="small"
                        />
                      )}
                      <Tooltip title={t('request:interaction.button.editArticle')}>
                        <IconButton
                          size="small"
                          sx={{ minWidth: 0, p: 0.5 }}
                          onClick={() => handleOpenArticleDrawer(record.article.self)}
                        >
                          <Edit fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )
                },
              },
              {
                title: t('request:content.label.quantityInProduct', {
                  defaultValue: 'Quantity in Product',
                }),
                key: 'quantity',
                width: 90,
                render: (_, record) => {
                  const quantityKey = getArticleQuantityKey(record.article.self, group.bomRootSelf)
                  const currentQuantity = articleQuantities[quantityKey] || record.quantity || 1
                  return <Typography variant="body2">{currentQuantity}</Typography>
                },
              },
              {
                title: t('request:content.label.additionalParts', {
                  defaultValue: 'Additional parts',
                }),
                key: 'additionalParts',
                width: 100,
                render: (_, record) => {
                  const additionalKey = getAdditionalQuantityKey(
                    record.article.self,
                    group.bomRootSelf
                  )
                  const currentAdditional = additionalQuantities[additionalKey] ?? 0

                  return (
                    <TextField
                      type="number"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={currentAdditional}
                      onChange={e => {
                        const rawValue = parseInt(e.target.value, 10)
                        const safeValue = Number.isNaN(rawValue) ? 0 : Math.max(0, rawValue)
                        setAdditionalQuantities(prev => ({
                          ...prev,
                          [additionalKey]: safeValue,
                        }))
                      }}
                      inputProps={{ min: 0, step: 1 }}
                    />
                  )
                },
              },
            ]

            return (
              <Box key={group.bomRootSelf || groupIndex} sx={{ mt: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  {group.bomRootName} ({t('request:content.label.bomArticles')})
                </Typography>

                {/* BOM Root Article Row */}
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    border: '2px solid',
                    borderColor: hasRootFiles ? 'primary.main' : 'error.main',
                    borderRadius: 1,
                    backgroundColor: hasRootFiles
                      ? 'rgba(25, 118, 210, 0.08)'
                      : 'rgba(211, 47, 47, 0.08)',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t('request:content.label.bomRootArticle')}:
                      </Typography>
                      <Typography variant="body1">
                        {group.bomRootArticle.name || group.bomRootArticle.number || '-'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ({group.bomRootArticle.number})
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {hasRootFiles ? (
                        <Chip
                          icon={<CheckCircle />}
                          label={t('request:content.label.hasFiles')}
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<ErrorIcon />}
                          label={t('request:content.label.noFiles')}
                          color="error"
                          size="small"
                        />
                      )}

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                          {t('request:content.label.bomQuantity')}:
                        </Typography>
                        <TextField
                          type="number"
                          variant="outlined"
                          size="small"
                          sx={{ width: 100 }}
                          value={rootQuantity}
                          onChange={e => {
                            const value = parseInt(e.target.value) || 1
                            if (value >= 1) {
                              handleBomRootQuantityChange(group.bomRootSelf, value)
                            }
                          }}
                          inputProps={{ min: 1, step: 1 }}
                        />
                      </Box>

                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleOpenArticleDrawer(group.bomRootSelf)}
                        title={t('request:interaction.button.editArticle')}
                      >
                        <Edit />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>

                {/* BOM Leaf Articles Table */}
                {group.articles.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreRounded />}>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                        {t('request:content.label.parts', { defaultValue: 'Parts' })} (
                        {group.articles.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Table
                        columns={bomLeafColumns}
                        dataSource={group.articles.map((item, index) => ({
                          ...item,
                          key: item.article.self || index,
                        }))}
                        pagination={false}
                        size="small"
                        bordered
                        scroll={{ x: 'max-content' }}
                        tableLayout="auto"
                      />
                    </AccordionDetails>
                  </Accordion>
                )}
              </Box>
            )
          })}

          {/* Regular Articles Table */}
          {regularArticles.length > 0 && (
            <Box sx={{ mt: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('request:content.label.articles')} ({regularArticles.length})
              </Typography>
              <Table
                columns={
                  [
                    {
                      title: t('request:content.label.nameWithNumber', {
                        defaultValue: 'Name (Nr.)',
                      }),
                      dataIndex: ['article', 'name'],
                      key: 'name',
                      width: 260,
                      render: (_: any, record: ArticleWithQuantity) => {
                        const hasFiles = articleHasFiles(record.article)
                        const articleName =
                          record.article.name ||
                          record.article.number ||
                          t('request:content.label.part', 'Part')
                        const numberSuffix =
                          record.article.name && record.article.number
                            ? ` (${record.article.number})`
                            : ''
                        const displayName = `${articleName}${numberSuffix}`

                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{displayName}</span>
                            {hasFiles ? (
                              <Chip
                                icon={<CheckCircle />}
                                label={t('request:content.label.hasFiles')}
                                color="success"
                                size="small"
                              />
                            ) : (
                              <Chip
                                icon={<ErrorIcon />}
                                label={t('request:content.label.noFiles')}
                                color="error"
                                size="small"
                              />
                            )}
                            <Tooltip title={t('request:interaction.button.editArticle')}>
                              <IconButton
                                size="small"
                                sx={{ minWidth: 0, p: 0.5 }}
                                onClick={() => handleOpenArticleDrawer(record.article.self)}
                              >
                                <Edit fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )
                      },
                    },
                    {
                      title: t('request:content.label.quantityInProduct', {
                        defaultValue: 'Quantity in Product',
                      }),
                      key: 'quantity',
                      width: 90,
                      render: (_: any, record: ArticleWithQuantity) => {
                        const quantityKey = getArticleQuantityKey(record.article.self)
                        const currentQuantity = articleQuantities[quantityKey] || 1
                        return <Typography variant="body2">{currentQuantity}</Typography>
                      },
                    },
                    {
                      title: t('request:content.label.additionalParts', {
                        defaultValue: 'Additional parts',
                      }),
                      key: 'additionalParts',
                      width: 100,
                      render: (_: any, record: ArticleWithQuantity) => {
                        const additionalKey = getAdditionalQuantityKey(record.article.self)
                        const currentAdditional = additionalQuantities[additionalKey] ?? 0

                        return (
                          <TextField
                            type="number"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={currentAdditional}
                            onChange={e => {
                              const rawValue = parseInt(e.target.value, 10)
                              const safeValue = Number.isNaN(rawValue) ? 0 : Math.max(0, rawValue)
                              setAdditionalQuantities(prev => ({
                                ...prev,
                                [additionalKey]: safeValue,
                              }))
                            }}
                            inputProps={{ min: 0, step: 1 }}
                          />
                        )
                      },
                    },
                  ] as ColumnsType<ArticleWithQuantity>
                }
                dataSource={regularArticles.map((item, index) => ({
                  ...item,
                  key: item.article.self || index,
                }))}
                pagination={false}
                size="small"
                bordered
                scroll={{ x: 'max-content' }}
                tableLayout="auto"
              />
            </Box>
          )}

          {/* Customer Price Suggestion Field */}
          {(bomArticleGroups.length > 0 || regularArticles.length > 0) && (
            <Box mt="2rem">
              <TextField
                fullWidth
                label={t('request:content.label.customerPriceSuggestion', {
                  defaultValue: 'Persönlicher Kostenvorschlag',
                })}
                value={(formState.state as any)?.customerPriceSuggestion ?? ''}
                onChange={e => {
                  const nextValue = e.target.value
                  if (!isValidPriceInput(nextValue)) {
                    setCustomerPriceError(true)
                    return
                  }
                  setCustomerPriceError(false)
                  formState.setState((prev: any) => ({
                    ...prev,
                    customerPriceSuggestion: nextValue,
                  }))
                }}
                error={customerPriceError}
                helperText={
                  customerPriceError
                    ? t('request:feedback.error.onlyNumbers', {
                        defaultValue: 'Nur Zahlen erlaubt',
                      })
                    : ' '
                }
                InputProps={{
                  inputMode: 'decimal',
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <span>€</span>
                        <Tooltip
                          title={t('request:content.description.customerPriceSuggestion', {
                            defaultValue:
                              'Tragen Sie hier Ihren persönlichen Preisvorschlag für den Stückpreis ein. Dies hilft uns, Ihre Erwartung besser einzuordnen und ein passendes Angebot zu erstellen.',
                          })}
                        >
                          <IconButton size="small" edge="end">
                            <HelpOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}

          <Form
            formObject={formState.state ?? {}}
            formFields={requestFormFields}
            onChange={(value: FormObject) => formState.setState(value as Request)}
            editing={true}
          />
          {/* Show warning if not all articles have files */}
          {!hasAllArticlesFiles() &&
            (bomArticleGroups.length > 0 || regularArticles.length > 0) && (
              <Box sx={{ mt: 2, mb: 1 }}>
                <Alert severity="error" sx={{ mb: 1 }}>
                  {t('request:feedback.error.someArticlesMissingFiles')}
                </Alert>
              </Box>
            )}

          {/* Backward compatibility: Single article warning */}
          {!hasArticleFiles() &&
            formState.state?.article &&
            typeof formState.state.article !== 'string' &&
            articleSelfs.length === 1 &&
            bomArticleGroups.length === 0 &&
            regularArticles.length === 0 && (
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  {t('request:feedback.error.articleMustHaveFile')}
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => handleOpenArticleDrawer()}
                >
                  {t('request:interaction.button.uploadArticleFile')}
                </Button>
              </Box>
            )}
          {isPrivileged(user, 'STAFF') && (
            <Grid sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ marginTop: '15px', marginBottom: '10px' }}>
                {t('common:content.label.documents')}
              </Typography>
              <FileInput
                inputState={{ state: fileInput, setState: setFileInput }}
                maxAllowedFiles={10}
              />
              {fileInput.length > 0 && (
                <InputFileList
                  files={fileInput}
                  noFilesInfo=""
                  onRemoveFileFromList={handleRemoveFileFromList}
                  onReUploadFile={handleReUploadFile}
                  onDeleteFile={handleDeleteFile}
                />
              )}
            </Grid>
          )}
        </React.Fragment>
      </MultilevelDrawer>
      <ArticleDrawer
        open={openArticleDrawer}
        setOpen={setOpenArticleDrawer}
        formState={{
          state: articleFormInput,
          setState: setArticleFormInput,
        }}
        onCreateConfirm={async () => {
          // This should not be called in edit mode
          return Promise.resolve()
        }}
        enableEditMode={true}
        onEditConfirm={handleUpdateArticle}
        onClose={handleArticleDrawerClose}
      />
    </>
  )
}
